/**
 * Reference data for the HP service codes this plugin can send.
 *
 * HP has no codebook endpoint -- the DxWeb API exposes only ping, client_auth,
 * create/cancel_shipment_orders, fetch_shipment_status, fetch_shipping_labels
 * and fetch_parcel_delivery_point. The `service`, `delivery_type`,
 * `pickup_type` and `parcel_size` enums exist only as prose in
 * docs/HP-Shipping_Service_Api_Documentation.docx, so they are transcribed
 * here and rendered by ServiceGuideModal.
 *
 * Sources, so this can be re-checked when HP reissues the documentation:
 *   - codes and field rules: CreateShipmentOrders / ShipmentOrderData table
 *   - EasyReturn 39 / 40 / 45 mechanics: Appendix E
 *   - EasyReturn payer rules: Appendix C (API validation errors)
 *   - D+n meaning: https://www.posta.hr/saljem-u-hrvatsku and
 *     https://www.posta.hr/rokovi-urucenja-paket24-posiljaka/1384
 *
 * `code` values must stay in step with SERVICE_OPTIONS in CreateLabelModal.jsx
 * and the accepted lists in server/store.js.
 */

/**
 * What "D+3" actually counts, which is the question the codes raise and
 * neither HP's API docs nor the plugin answered before.
 */
export const DEADLINE_EXPLAINER = {
  title: 'What D+1, D+2 and D+3 mean',
  body:
    'D+n is the delivery deadline in working days, counted from the day after HP takes the parcel over -- the hand-over day itself does not count. Weekends, holidays and days without delivery are excluded, as is time lost to an incomplete address or to circumstances outside HP’s control.',
  bullets: [
    'Paket24 reaches the next working day (D+1) in the 200+ settlements on HP’s D+1 list, two working days elsewhere.',
    'Parcels addressed to the islands get one extra day, as do parcels handed over after that day’s cut-off time.',
    'The tier you pick is what you are contractually promised and billed for. It does not make an individual parcel travel faster than the route allows.',
  ],
};

export const SERVICES = [
  {
    code: 26,
    name: 'Paket24 D+1',
    shortName: 'Express',
    directions: ['OUTBOUND', 'RETURN'],
    summary: 'Fastest Paket24 tier: delivery the next working day.',
    detail:
      'Guaranteed next working day in the settlements on HP’s D+1 list (200+ places, mostly larger towns and cities). Addresses outside that list are still delivered as fast as the route allows, but the D+1 promise does not apply to them.',
    bestFor: 'Orders you have promised to deliver tomorrow, and anything time-sensitive.',
  },
  {
    code: 29,
    name: 'Paket24 D+2',
    shortName: 'Standard',
    directions: ['OUTBOUND', 'RETURN'],
    summary: 'Everyday Paket24 tier: delivery within two working days.',
    detail:
      'The tier most e-commerce parcels use. Two working days covers the whole mainland; island addresses get the usual extra day.',
    bestFor: 'The normal choice for shipping to customers. This is the plugin’s outbound default.',
    recommendedFor: 'OUTBOUND',
  },
  {
    code: 32,
    name: 'Paket24 D+3',
    shortName: 'Economy',
    directions: ['OUTBOUND', 'RETURN'],
    summary: 'Slowest Paket24 tier: delivery within three working days.',
    detail:
      'Same network and same tracking as the faster tiers, with a longer guaranteed deadline and a lower price. Three working days is also the statutory deadline for ordinary parcels.',
    bestFor: 'Bulky, low-margin or non-urgent orders where a day either way does not matter.',
  },
  {
    code: 40,
    name: 'EasyReturn D+3 (2nd option)',
    shortName: 'EasyReturn, on request',
    directions: ['RETURN'],
    summary:
      'A return label created after the fact, when a customer actually asks to send something back.',
    detail:
      'You submit the return to HP at the moment the customer requests it and send them the label -- or just the shipment number. If the customer wants a courier to collect from their address, HP needs a collection date and time window; for post office or parcel locker drop-off those fields must stay empty. This is what the plugin sends when you create a return label from an order.',
    bestFor: 'Returns handled case by case, which is how most Shopify stores work.',
    recommendedFor: 'RETURN',
    rules: [
      'Paid by the recipient (you), on your contracted account -- HP rejects it otherwise.',
      'The customer can hand it over at any post office or parcel locker, or book an address collection.',
    ],
  },
  {
    code: 39,
    name: 'EasyReturn D+3 (1st option)',
    shortName: 'EasyReturn, prepaid in the box',
    directions: ['RETURN'],
    summary:
      'A return label created up front and packed in the outgoing box, before you know whether it will be used.',
    detail:
      'You submit both shipments to HP and print both labels; the return label travels inside the parcel. HP ties the two together through reference_field_B, which must carry the same value on the outgoing and the return shipment. The customer either drops the parcel off with the enclosed label, or enters the shipment number at posiljka.posta.hr and books an address collection.',
    bestFor:
      'Fashion and other high-return categories where a no-questions-asked return label in the box is part of the offer.',
    rules: [
      'Paid by the recipient (you), on your contracted account.',
      'Needs the matching outgoing shipment: same reference_field_B on both.',
      'The plugin creates the two labels separately, so you must set that reference yourself if you use this code.',
    ],
  },
];

/**
 * Additional service 45 -- the third EasyReturn variant. It is not a `service`
 * code but an entry in AdditionalServiceList on an outgoing shipment, so it is
 * listed apart from the codes above. The plugin does not send it today.
 */
export const EASY_RETURN_45 = {
  name: 'Additional service 45 -- EasyReturn without a return label',
  summary:
    'Marks an outgoing parcel as returnable without printing a second label at all.',
  detail:
    'You submit only the outgoing shipment, flagged with additional service 45. Nothing extra goes in the box. When a customer wants to return the goods they enter the outgoing shipment number at posiljka.posta.hr, press the return button, and HP gives them a return shipment number they quote at any post office or parcel locker -- or they book an address collection there instead.',
  note: 'Not supported by this plugin yet; it would be set on the outgoing label rather than as a return service.',
};

export const DELIVERY_TYPES = [
  {
    value: 1,
    name: 'To the address',
    detail: 'A courier delivers to the customer’s door. No centre code needed.',
  },
  {
    value: 2,
    name: 'To a post office (poštanski ured)',
    detail:
      'The parcel waits at the post office you name. The recipient has five working days to collect it before HP starts returning it to you. Needs the post office code.',
  },
  {
    value: 3,
    name: 'To a parcel locker (paketomat)',
    detail:
      'Collection around the clock from the locker you name, again within five working days. Needs the paketomat code, a parcel size, and a Croatian mobile number for the PIN. HP does not allow cash on delivery to a locker, and caps a locker parcel at 30 kg.',
  },
];

export const PICKUP_TYPES = [
  {
    value: 1,
    name: 'Address pickup',
    detail:
      'A courier collects from the sender’s address. On a return created after the fact (service 40) HP needs a collection date and a time window.',
  },
  {
    value: 2,
    name: 'Post office drop-off',
    detail: 'The sender hands the parcel in at a post office. Needs the post office code.',
  },
  {
    value: 3,
    name: 'Parcel locker drop-off',
    detail:
      'The sender puts the parcel into a paketomat. Needs the paketomat code and a parcel size, and the locker rules above apply.',
  },
];

/**
 * Compartment sizes, from the parcel_size enum. Required whenever a parcel
 * locker is either end of the journey, and only one size may be given.
 */
export const PARCEL_SIZES = [
  { value: 'XS', dims: '9 × 16 × 64 cm' },
  { value: 'S', dims: '9 × 38 × 64 cm' },
  { value: 'M', dims: '19 × 38 × 64 cm' },
  { value: 'L', dims: '39 × 38 × 64 cm' },
];
